<script>
import api from '@/api'
import { alertSuccess } from '@/helpers/swal'

export default {
  data() {
    return {
      pagetitle: 'Users',
      roles: [],
      selectedRole: '',
      user: {
        id: null,
        username: '',
        password: '',
        dateCreated: Date.now(),
        fullName: '',
        email: '',
        passwordResetHash: null,
        passwordResetToken: null,
        themePreference: null,
        role: {},
        confirmPassword: '',
        adminGroup: {
          id: null,
          type: null,
          parent: null,
          name: null,
          isSystem: false,
          location: null,
          childrenSize: null,
          hasChildren: false,
          isLeafType: false,
        },
      },
      errors: {
        username: '',
        fullName: '',
        role: '',
        email: '',
        password: '',
        confirmPassword: '',
      },
      saveBtnDisabled: false,
    }
  },
  async mounted() {
    await this.getRoles()
    // In case of edit user
    const userId = this.$route.params.id
    if (userId) {
      await this.getUserData(userId)
    }
  },
  methods: {
    async getRoles() {
      await api.get(`/admin/roles/${this.authUser.gid}/list.json`).then((response) => {
        this.roles = response.data?.result || []
      })
    },

    async getUserData(userId) {
      await api.get(`/admin/accounts/get/${userId}.json`).then((response) => {
        this.user = response.data.object
        this.selectedRole = this.user.role.id
      })
    },

    saveUser() {
      if (!this.validateRequest()) return

      this.saveBtnDisabled = true
      let data = this.user
      data.role = this.roles.find((role) => role.id === this.selectedRole)
      this.user.id ? this.updateUser(data) : this.addUser(data)
    },

    addUser(data) {
      api
        .post('/admin/accounts/add', data)
        .then(() => {
          alertSuccess('User added successfully')
          this.$router.push({ name: 'users' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    updateUser(data) {
      api
        .post('/admin/accounts/update.json', data)
        .then(() => {
          alertSuccess('User updated successfully')
          this.$router.push({ name: 'users' }) 
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    validateRequest() {
      let isValid = true
      this.errors = {}
      const { id, username, fullName, email, password, confirmPassword } = this.user
      if (!username) {
        this.errors.username = 'Username is required'
        isValid = false
      }
      if (!fullName) {
        this.errors.fullName = 'Name field is required'
        isValid = false
      }
      if (!this.selectedRole) {
        this.errors.role = 'Role is required'
        isValid = false
      }
      if (!email) {
        this.errors.email = 'Email is required'
        isValid = false
      }
      if (!id && !password) {
        this.errors.password = 'Password is required'
        isValid = false
      }
      if (!id && !confirmPassword) {
        this.errors.confirmPassword = 'Password confirmation is required'
        isValid = false
      }
      if (!id && password !== confirmPassword) {
        this.errors.confirmPassword = "Password confirmation doesn't match"
        isValid = false
      }
      return isValid
    },
  },
}
</script>

<template>
  <div class="row">
    <div class="col-lg-6">
      <Teleport to="#elePageTitle">
        {{ pagetitle }}
      </Teleport>
      <div class="card">
        <div class="card-header d-flex align-items-center">
          <h4 class="card-title">{{ $route.params.id ? 'Edit User' : 'Add New User' }}</h4>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveUser()">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="username-input" class="form-label">Username</label>
                <input type="text" class="form-control" placeholder="Enter username" id="username-input" autocomplete="off" v-model="user.username" :disabled="user.id" />
                <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label for="name-input" class="form-label">Name</label>
                <input type="text" class="form-control" placeholder="Enter name" id="name-input" v-model="user.fullName" />
                <div v-if="errors.fullName" class="invalid-feedback">{{ errors.fullName }}</div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="role-input" class="form-label">Role</label>
                <select id="role-input" class="form-select" v-model="selectedRole" required>
                  <option selected disabled value="">Select</option>
                  <option v-for="(role, index) in roles" :key="index" :value="role.id">{{ role.roleName }}</option>
                </select>
                <div v-if="errors.role" class="invalid-feedback">{{ errors.role }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label for="email-input" class="form-label">Enter Email</label>
                <input type="email" class="form-control" placeholder="Email" id="email-input" autocomplete="off" v-model="user.email" required />
                <div v-if="errors.email" class="invalid-feedback">{{ errors.email }}</div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3 mb-md-0">
                <label for="password-input" class="form-label">Password</label>
                <input type="password" class="form-control" placeholder="Enter Password" id="password-input" autocomplete="off" v-model="user.password" />
                <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
              </div>
              <div class="col-md-6">
                <label for="confirm-password-input" class="form-label">Confirm Password</label>
                <input type="password" class="form-control" placeholder="Enter Password" id="confirm-password-input" v-model="user.confirmPassword" />
                <div v-if="errors.confirmPassword" class="invalid-feedback">{{ errors.confirmPassword }}</div>
              </div>
            </div>
          </form>
        </div>
        <div class="card-footer">
          <div class="row">
            <div class="col-7">
              <router-link to="/users" class="btn btn-light d-inline-flex align-items-center">
                <i class="bx bx-arrow-back font-size-16 me-1"></i>
                Back to List
              </router-link>
            </div>
            <div class="col-5 d-flex justify-content-end">
              <button class="btn btn-success d-flex align-items-center" @click.prevent="saveUser()" :disabled="saveBtnDisabled">
                <i v-if="!saveBtnDisabled" class="bx bx-check-double font-size-16"></i>
                <i v-else class="bx bx-loader bx-spin font-size-16"></i>
                <span class="ms-1">Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- end card -->
    </div>
  </div>
  <!-- </Layout> -->
</template>
